import * as React from 'react';
import { useState } from 'react';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { changeSail } from '../../../actions/cruisePageActions';
import { checkForDirectBooking } from '../../../actions/requestFormActions';
import { CabinsSection } from '../../../components/CabinsSection/CabinsSection';
import { TrackComponent } from '../../../components/TrackComponent';
import { IState } from '../../../reducers';
import { PriceHeader } from './PriceHeader';
import { SailSelect } from './SailSelect';
import { __ } from '../../../helpers/TranslationService';
import { PassengerSelectSection } from './PassengerSelectSection';
import { useActions } from '../../../hooks/useActions';

export interface ICabinTableProps {
    isSubmitted?: boolean;
    cheapestCabinId: number;
    onCabinSelection?: (event: any) => void;
    selectedCabinId?: number;
    cabinSectionRef?: React.MutableRefObject<HTMLDivElement> | null;
}

interface IComponentState {
    collapsed: {
        inside: boolean;
        outside: boolean;
        balcony: boolean;
        suite: boolean;
    };
}

const StyledCabinTitle = styled.p`
    margin: 34px 0 0 0;
    font-weight: bold;
    font-size: 16px;
`;

const Wrapper = styled.div`
    padding: 0 16px;
    background: ${(props) => props.theme.colors.desertStorm[100]};
`;

export const CabinTable = (props: ICabinTableProps) => {
    const dispatch = useDispatch();

    const {
        cruisePage,
        router,
        requestForm,
        discountThreshold,
        currency,
        dualCurrency,
    } = useSelector((state: IState) => ({
        cruisePage: state.cruisePage,
        requestForm: state.requestForm,
        router: state.router,
        discountThreshold: state.pageData.discountThreshold,
        currency: state.pageData.appConfig.defaultCurrency,
        dualCurrency: state.pageData.appConfig.dualCurrency,
    }));

    const { setAdultsNum, setChildrenNum, setChildAge } = useActions();

    const [collapse, setCollapse] = useState<IComponentState>({
        collapsed: { inside: true, outside: true, balcony: true, suite: true },
    });

    const {
        sails,
        selectedSailIndex,
        rates,
        processedCabins,
        ratesBySail,
        soldOutBooking,
        includesFlight,
    } = cruisePage;
    const TrackableSailSelect = TrackComponent(SailSelect, { category: 'mobile-productpage' });

    const sailOptions = sails.map((sail, id) => ({ label: sail.label, nid: sail.nid, id }));
    const cabins = processedCabins;
    const sailNid = sails[selectedSailIndex]?.nid;

    const numberOfSails = sails?.length;

    const defaultCurrency = dualCurrency || currency;

    const collapseAllRows = () => {
        setCollapse({
            collapsed: {
                inside: true,
                outside: true,
                balcony: true,
                suite: true,
            },
        });
    };

    const handleSailChange = (index) => {
        dispatch(changeSail(index));
        dispatch(checkForDirectBooking());
        collapseAllRows();
    };

    return (
        <Wrapper id="cabinTable" ref={props.cabinSectionRef}>
            <PassengerSelectSection
                cruisePage={cruisePage}
                requestForm={requestForm}
                router={router}
                setAdultsNum={setAdultsNum}
                setChildrenNum={setChildrenNum}
                setChildAge={setChildAge}
                isSubmitted={props.isSubmitted}
            />

            <PriceHeader numberOfSails={numberOfSails} />

            <TrackableSailSelect
                selectedId={selectedSailIndex}
                options={sailOptions}
                onChange={handleSailChange}
                rates={rates}
                ratesBySail={ratesBySail}
                currency={defaultCurrency}
            />

            {cabins && (
                <>
                    <StyledCabinTitle>{__(`Cabin type`, 'dreamlines')}</StyledCabinTitle>

                    <CabinsSection
                        cabins={cabins}
                        sailNid={sailNid}
                        router={router}
                        cruisePage={cruisePage}
                        discountThreshold={discountThreshold}
                        isPriceShown={true}
                        isCabinSelectShown={true}
                        cheapestCabinId={props.cheapestCabinId}
                        onCabinSelection={(e) => props.onCabinSelection(e)}
                        selectedCabinId={props.selectedCabinId}
                        departure={sailNid > 0 ? sails[selectedSailIndex].departure : undefined}
                        soldOutBooking={soldOutBooking}
                        includesFlight={includesFlight}
                    />
                </>
            )}
        </Wrapper>
    );
};
